import { useRef } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useAppState } from '../../AppStateProvider';
import { appStore } from '../../services/appStore';
import { useUseCases } from '../../UseCaseProvider';

import { useBulkActionsTracking } from './BulkActionBar/useBulkActionsTracking';

type HookParams = {
  onRevealFinish: VoidFunction;
};

type HookValue = {
  isPending: boolean;
  areAllContactsRevealed: boolean;
  onBulkReveal: VoidFunction;
};

export const useBulkReveal = ({ onRevealFinish }: HookParams): HookValue => {
  const { t } = useTranslation();

  const { isMasterCheckboxChecked, selectedContacts } = useAppState();

  const contactsToRevealCount = selectedContacts.filter((contact) => !contact.isRevealed).length;

  const allContactsAreRevealed = selectedContacts.length > 0 && contactsToRevealCount === 0;
  const { revealContactsBulk } = useUseCases();

  const trackingContextRef = useRef<{
    count: number;
    isMaster: boolean;
  } | null>(null);

  const { handleTrackSuccessReveal, handleTrackFailReveal } = useBulkActionsTracking();

  const handleRevealFinish = () => {
    handleTrackSuccessReveal(trackingContextRef.current);
    onRevealFinish();
  };

  const handleRevealError = () => {
    handleTrackFailReveal(trackingContextRef.current);
    appStore.setErrorToast({
      content: t('contacts.bulk_reveal.failed'),
      showCloseIcon: true,
    });
  };

  const { mutate, isPending } = useMutation({
    mutationFn: revealContactsBulk,
    onSuccess: handleRevealFinish,
    onError: handleRevealError,
    onSettled: () => {
      trackingContextRef.current = null;
    },
  });

  const handleBulkReveal = () => {
    trackingContextRef.current = {
      count: selectedContacts.length,
      isMaster: isMasterCheckboxChecked,
    };
    mutate({ contactsToReveal: selectedContacts });
    appStore.setSelectedContacts([]);
  };

  return {
    areAllContactsRevealed: allContactsAreRevealed,
    isPending,
    onBulkReveal: handleBulkReveal,
  };
};
